import { LeasingCalculatorSharedFormProps } from 'modules/leasingCalculator/components/common/LeasingCalculatorData';
import { useLeasingInstallmentCalculation } from 'modules/leasingCalculator/components/infoCalculation/components/common/useLeasingInstallmentCalculation';
import { LeasingType } from 'modules/types/LeasingOffer';
import { InputWithLabel, Subtitle, Tooltip, theme } from 'modules/ui';
import { StyledTooltipIcon } from 'modules/ui/select/styled';
import { SliderWithLabel } from 'modules/ui/slider/SliderWithLabel';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { SufixType } from 'modules/ui/input/common/SufixType';
import { parseFromCurrencyString, parseToCurrencyString } from 'modules/utils';
import { numberToLocaleString } from 'modules/utils/numberFormatUtils';
import { useForm } from 'modules/ui/form/useForm';
import { FooterLine, LeasingDataContainer } from '../../styled';
import { LeasingInstallmentInfoCard } from '../common/installmentInfoCard';
import { LeasingInstallmentState } from '../common/LeasingInstallmentState';
import { FooterButtons } from '../common/footerButtons';
import { OperationalFutureValuePercentageMap } from '../common/helper';
import { useLeasingCalculatorCodes } from '../../../../../hooks/codesHooks/useLeasingCalculatorCodes';

export const OperationalLeasingData: React.FC<
  LeasingCalculatorSharedFormProps
> = ({ defaultValues, onChange }, props) => {
  const { t } = useTranslation();

  const getDefaultFutureValuePct = () => {
    if (defaultValues?.operationalFutureValuePercentage) {
      return +defaultValues.operationalFutureValuePercentage;
    }
    const period = defaultValues?.financingPeriod?.id;
    if (period && OperationalFutureValuePercentageMap[+period]) {
      return OperationalFutureValuePercentageMap[+period];
    }
    return 0;
  };

  const [futureValuePct, setFutureValuePct] = useState<number>(
    getDefaultFutureValuePct(),
  );

  // Update default when financing period changes
  useEffect(() => {
    const period = defaultValues?.financingPeriod?.id;
    if (period && OperationalFutureValuePercentageMap[+period]) {
      setFutureValuePct(OperationalFutureValuePercentageMap[+period]);
    }
  }, [defaultValues?.financingPeriod?.id]);

  const operationalInitialValue: LeasingInstallmentState<string> = {
    leasingInstallment: '0',
    leasingParticipationAmount: '0',
    futureValuePercentage: '0',
    futureValue: '0',
  };

  const {
    formData,
    recalculateFutureValuePercentage,
    recalculateParticipationPercentage,
  } = useLeasingInstallmentCalculation(
    defaultValues,
    operationalInitialValue,
    LeasingType.OPERATIONAL,
    defaultValues?.operationalLeasingParticipation,
    futureValuePct,
  );

  const { updateFormData } = useForm({
    onChange,
  });

  const { findLeasingTypeByName } = useLeasingCalculatorCodes();

  // Admin cost: bidirectional percentage <-> amount (OL uses neto base)
  const netoTotalPrice = useMemo(() => {
    const netPrice = +(defaultValues?.leasingNetObjectPrice || 0);
    const ppmv = +(defaultValues?.leasingObjectPpmv || 0);
    return netPrice + ppmv;
  }, [defaultValues?.leasingNetObjectPrice, defaultValues?.leasingObjectPpmv]);

  const [adminCostAmount, setAdminCostAmount] = useState<string>(() => {
    const pct = +(defaultValues?.operationalAdminCostPercentage ?? 0) / 100;
    return numberToLocaleString(pct * netoTotalPrice);
  });

  useEffect(() => {
    const pct = +(defaultValues?.operationalAdminCostPercentage ?? 0) / 100;
    setAdminCostAmount(numberToLocaleString(pct * netoTotalPrice));
  }, [defaultValues?.operationalAdminCostPercentage, netoTotalPrice]);

  const handleAdminCostAmountChange = (value: string) => {
    const amount = +parseFromCurrencyString(value);
    if (netoTotalPrice > 0) {
      const newPct = (amount / netoTotalPrice) * 100;
      updateFormData('operationalAdminCostPercentage', newPct);
    }
  };

  useEffect(() => {
    if (onChange) {
      onChange({
        operationalLeasingInstallment: formData.leasingInstallment,
        operationalParticipationAmount: formData.leasingParticipationAmount,
        operationalFutureValuePercentage: formData.futureValuePercentage,
        operationalFutureValue: formData.futureValue,
      });
    }
  }, [
    formData.leasingInstallment,
    formData.leasingParticipationAmount,
    formData.futureValuePercentage,
    formData.futureValue,
  ]);

  const handleFutureValuePercentageChange = (value: string) => {
    const parsed = +parseFromCurrencyString(value);
    setFutureValuePct(parsed);
  };

  const handleFutureValueAmountChange = (value: string) => {
    const newPct = recalculateFutureValuePercentage(value);
    if (newPct !== undefined) {
      setFutureValuePct(newPct);
    }
  };

  const handleParticipationAmountChange = (value: string) => {
    const newPct = recalculateParticipationPercentage(value);
    if (newPct !== undefined) {
      updateFormData('operationalLeasingParticipation', newPct);
    }
  };

  return (
    <LeasingDataContainer>
      <Subtitle>
        {t('leasingCalculator.infoCalculation.operationalLeasing.title')}
        <Tooltip
          placement="topRight"
          title={t(
            'leasingCalculator.infoCalculation.operationalLeasing.titleTooltip',
          )}
          color={theme.colors.primaryK10}>
          <StyledTooltipIcon />
        </Tooltip>
      </Subtitle>

      <SliderWithLabel
        label={t(
          'leasingCalculator.infoCalculation.generalData.adminCostPercentage',
        )}
        min={0}
        max={5}
        valueType={SufixType.Percentage}
        defaultValue={defaultValues?.operationalAdminCostPercentage}
        parser={(value) => value && parseToCurrencyString(value)}
        onChange={(value) =>
          updateFormData(
            'operationalAdminCostPercentage',
            parseFromCurrencyString(value),
          )
        }
      />
      <InputWithLabel
        label={t(
          'leasingCalculator.infoCalculation.generalData.adminCostAmount',
        )}
        defaultValue={adminCostAmount}
        sufix={SufixType.Euro}
        onBlur={(e) => handleAdminCostAmountChange(e.target.value)}
      />

      <SliderWithLabel
        label={t(
          'leasingCalculator.infoCalculation.operationalLeasing.advancePaymentPercentage',
        )}
        labelTooltip={t(
          'leasingCalculator.infoCalculation.operationalLeasing.advancePaymentPercentageTooltip',
        )}
        min={0}
        max={50}
        valueType={SufixType.Percentage}
        defaultValue={defaultValues?.operationalLeasingParticipation}
        parser={(value) => value && parseToCurrencyString(value)}
        onChange={(value) =>
          updateFormData(
            'operationalLeasingParticipation',
            parseFromCurrencyString(value),
          )
        }
      />
      <InputWithLabel
        label={t(
          'leasingCalculator.infoCalculation.operationalLeasing.participationAmount',
        )}
        defaultValue={formData.leasingParticipationAmount}
        sufix={SufixType.Euro}
        onBlur={(e) => handleParticipationAmountChange(e.target.value)}
      />
      <SliderWithLabel
        label={t(
          'leasingCalculator.infoCalculation.operationalLeasing.futureValuePercentage',
        )}
        labelTooltip={t(
          'leasingCalculator.infoCalculation.operationalLeasing.futureValuePercentageTooltip',
        )}
        min={0}
        max={90}
        valueType={SufixType.Percentage}
        defaultValue={futureValuePct}
        parser={(value) => value && parseToCurrencyString(value)}
        onChange={handleFutureValuePercentageChange}
      />
      <InputWithLabel
        label={t(
          'leasingCalculator.infoCalculation.operationalLeasing.futureValue',
        )}
        defaultValue={formData.futureValue}
        sufix={SufixType.Euro}
        onBlur={(e) => handleFutureValueAmountChange(e.target.value)}
      />

      <LeasingInstallmentInfoCard
        installmentAmount={formData.leasingInstallment}
        leasingType={LeasingType.OPERATIONAL}
      />

      <FooterLine />

      <FooterButtons
        defaultValues={defaultValues}
        onChange={onChange}
        findLeasingTypeByName={findLeasingTypeByName}
        type={LeasingType.OPERATIONAL}
      />
    </LeasingDataContainer>
  );
};
